import React from "react";
import Welcome from "./Welcome";
import { trackOnce } from "../lib/analytics";
import Onboarding from "./Onboarding";
import RatingPrompt from "./RatingPrompt";
import Analysis from "./Analysis";
import PartialReveal from "./PartialReveal";
import Paywall from "./Paywall";
import Referral from "./Referral";
import FullReveal from "./FullReveal";
import DeadEnd from "./DeadEnd";
import EmailCapture from "./EmailCapture";
import ProfileBuilder from "./ProfileBuilder";
import WeeklyPicks from "./WeeklyPicks";
import PublicProfile from "./PublicProfile";
import PostPurchase from "./PostPurchase";
import Membership from "./Membership";
import AIChat from "./AIChat";
import DatingMatches from "./dating/Matches";
import MatchDetail from "./dating/MatchDetail";
import NotificationsInbox from "./NotificationsInbox";
import { STEPS } from "../hooks/useOnboardingFlow";

/**
 * Pure renderer — given current step + flow state/handlers, returns the right screen.
 * No state, no effects, no business logic. Easy to extend with a new step.
 */
export default function StepRenderer({ flow }) {
    const {
        step, user, archetype, idealPartner, viewingProfileId, viewingMatchId, foundingMemberNumber,
        setStep, setViewingProfileId, setViewingMatchId, setUser,
        onboardingDone, handleCheckoutSuccess, handleCodeRedeemed, handlePaidUnlock, dismissPaywall, handleReferralUnlocked, refreshUser,
    } = flow;

    switch (step) {
        case STEPS.WELCOME:
            return <Welcome onBegin={() => { trackOnce("assessment_started"); setStep(STEPS.ONBOARDING); }} />;

        case STEPS.ONBOARDING:
            return <Onboarding user={user} onComplete={onboardingDone} onExit={() => setStep(STEPS.WELCOME)} />;

        case STEPS.RATING:
            return <RatingPrompt onContinue={() => setStep(STEPS.ANALYZING)} />;

        case STEPS.ANALYZING:
            return <Analysis onDone={() => setStep(STEPS.REVEAL)} />;

        case STEPS.REVEAL:
            return (
                <PartialReveal
                    archetype={archetype}
                    user={user}
                    onUnlockPaid={() => setStep(user?.email && user?.location ? STEPS.PAYWALL : STEPS.EMAIL_CAPTURE)}
                    onUnlockReferral={() => setStep(STEPS.REFERRAL)}
                />
            );

        case STEPS.EMAIL_CAPTURE:
            return (
                <EmailCapture
                    user={user}
                    onCaptured={(email) => {
                        setUser(prev => ({ ...prev, email }));
                        setStep(STEPS.PAYWALL);
                    }}
                    onBack={() => setStep(STEPS.REVEAL)}
                />
            );

        case STEPS.PAYWALL:
            return (
                <Paywall
                    archetype={archetype}
                    sessionId={user?.session_id}
                    onSuccess={handleCheckoutSuccess}
                    onPaid={handlePaidUnlock}
                    onDismiss={dismissPaywall}
                    onRedeemed={handleCodeRedeemed}
                />
            );

        case STEPS.PAYWALL_DISCOUNT:
            return (
                <Paywall
                    archetype={archetype}
                    sessionId={user?.session_id}
                    onSuccess={handleCheckoutSuccess}
                    onPaid={handlePaidUnlock}
                    onDismiss={() => setStep(STEPS.REVEAL)}
                    onRedeemed={handleCodeRedeemed}
                    isDiscount
                />
            );

        case STEPS.REFERRAL:
            return (
                <Referral
                    user={user}
                    onBackToPaywall={() => setStep(STEPS.PAYWALL)}
                    onUnlocked={handleReferralUnlocked}
                />
            );

        case STEPS.UNLOCKED:
            return (
                <FullReveal
                    archetype={archetype}
                    idealPartner={idealPartner}
                    user={user}
                    onBuildProfile={() => setStep(STEPS.PROFILE_BUILDER)}
                    onBrowseMatches={() => setStep(STEPS.MATCHES)}
                    onOpenDating={() => setStep(STEPS.DATING_MATCHES)}
                    onOpenNotifications={() => setStep(STEPS.NOTIFICATIONS)}
                    onOpenCoach={() => setStep(STEPS.AI_COACH)}
                    onOpenConcierge={() => setStep(STEPS.AI_CONCIERGE)}
                    onOpenMembership={user?.founding_member_number ? () => setStep(STEPS.MEMBERSHIP) : null}
                />
            );

        case STEPS.PROFILE_BUILDER:
            return (
                <ProfileBuilder
                    user={user}
                    archetype={archetype}
                    onComplete={async () => {
                        await refreshUser();
                        setStep(STEPS.UNLOCKED);
                    }}
                />
            );

        case STEPS.MATCHES:
            return (
                <WeeklyPicks
                    user={user}
                    onBack={() => setStep(STEPS.UNLOCKED)}
                    onOpenProfile={(pid) => { setViewingProfileId(pid); setStep(STEPS.PUBLIC_PROFILE); }}
                    onOpenConcierge={() => setStep(STEPS.AI_CONCIERGE)}
                />
            );

        case STEPS.DATING_MATCHES:
            return (
                <DatingMatches
                    user={user}
                    onBack={() => setStep(STEPS.UNLOCKED)}
                    onOpenMatch={(mid) => { setViewingMatchId(mid); setStep(STEPS.DATING_MATCH); }}
                />
            );

        case STEPS.DATING_MATCH:
            return (
                <MatchDetail
                    user={user}
                    matchId={viewingMatchId}
                    onBack={() => setStep(STEPS.DATING_MATCHES)}
                />
            );

        case STEPS.NOTIFICATIONS:
            return (
                <NotificationsInbox
                    user={user}
                    onBack={() => setStep(STEPS.UNLOCKED)}
                    onOpenDating={() => setStep(STEPS.DATING_MATCHES)}
                />
            );

        case STEPS.AI_COACH:
            return <AIChat user={user} mode="coach" onBack={() => setStep(STEPS.UNLOCKED)} />;

        case STEPS.AI_CONCIERGE:
            return <AIChat user={user} mode="concierge" onBack={() => setStep(STEPS.UNLOCKED)} />;

        case STEPS.POST_PURCHASE:
            return (
                <PostPurchase
                    user={user}
                    foundingNumber={foundingMemberNumber}
                    onContinue={() => setStep(STEPS.UNLOCKED)}
                    onOpenMembership={() => setStep(STEPS.MEMBERSHIP)}
                />
            );

        case STEPS.MEMBERSHIP:
            return (
                <Membership
                    user={user}
                    onBack={() => setStep(user?.unlocked ? STEPS.UNLOCKED : STEPS.REVEAL)}
                />
            );

        case STEPS.PUBLIC_PROFILE:
            return (
                <PublicProfile
                    user={user}
                    publicId={viewingProfileId}
                    onBack={() => setStep(STEPS.MATCHES)}
                />
            );

        case STEPS.DEAD_END:
            return <DeadEnd />;

        default:
            return null;
    }
}
